export interface ResponseModel<T> {
    status: boolean;
    statusCode: number;
    statusDescription: string | null;
    data: T;
}

export interface WarrantyExtensionOption {
    durationMonths: number;
    available?: boolean;
    claimWindow?: string;
    price?: number;
}

export interface ProductSearchInfoDTO {
    brandId?: string;
    brandName?: string;
    categoryId?: string;
    categoryName?: string;
    productUrl?: string;
    mainImageLink?: string;
    taxonomy?: string;
    taxonomyName?: string[];
    categoryCodes?: string[];
    categoryNames?: string[];
}

export interface ServiceWarrantyDTO {
    serviceId?: string;
    productId: string;
    serviceProductId: string;
    productInfo?: ProductSearchInfoDTO;
    originalWarrantyMonths: number;
    price?: number;
    coveredParts?: string[];
    notCoveredParts?: string[];
    extensionOptions?: WarrantyExtensionOption[];
    claimReasons?: string[];
    claimRejectionReasons?: string[];
    resolutionModes?: string[];
    createdBy?: string;
    updatedBy?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface PagedServiceWarrantyResponse {
    total: number;
    page: number;
    size: number;
    records: ServiceWarrantyDTO[];
}

export type WarrantyStatus = 'ACTIVE' | 'EXPIRED';

export interface UserServiceWarrantyDTO {
    warrantyId: string;
    serviceId: string;
    productId: string;
    msn?: string;
    productName?: string;
    productImage?: string;
    variant?: string;
    quantity?: number;
    productInfo?: ProductSearchInfoDTO;
    orderId: number;
    orderItemId: number;
    orderPlacedAt?: string;
    deliveredAt?: string;
    amountPaid?: number;
    invoiceUrl?: string;
    userId: number;
    userName?: string;
    userPhone?: string;
    userEmail?: string;
    issuedAt: string;
    expiresAt: string;
    durationMonths: number;
    status: WarrantyStatus;
    daysLeftToExpire: number;
    claims?: WarrantyClaimDTO[];
    createdBy?: string;
    updatedBy?: string;
    createdAt?: string;
    updatedAt?: string;
}

export enum ClaimStage {
    CLAIM_SUBMITTED = 'CLAIM_SUBMITTED',
    VERIFIED_BY_MOGLIX = 'VERIFIED_BY_MOGLIX',
    UNDER_BRAND_REVIEW = 'UNDER_BRAND_REVIEW',
    CLAIM_APPROVED = 'CLAIM_APPROVED',
    NOT_APPROVED = 'NOT_APPROVED',
    ACTION_NEEDED = 'ACTION_NEEDED',
    DECLINED = 'DECLINED',
    PICKUP_SCHEDULED = 'PICKUP_SCHEDULED',
    VISIT_SCHEDULED = 'VISIT_SCHEDULED',
    PICKED_UP_INSPECTION = 'PICKED_UP_INSPECTION',
    ON_THE_WAY_SERVICE = 'ON_THE_WAY_SERVICE',
    OUTCOME_DECLARED = 'OUTCOME_DECLARED',
    CLOSED = 'CLOSED',
    CLOSED_ALTERNATIVES_SHOWN = 'CLOSED_ALTERNATIVES_SHOWN',
    CANCELLED = 'CANCELLED',
}

export enum ClaimOutcome {
    REPAIRED = 'REPAIRED',
    REPLACED = 'REPLACED',
    NOT_COVERED = 'NOT_COVERED',
    RESOLVED = 'RESOLVED',
    PICKUP_NEEDED = 'PICKUP_NEEDED',
}

export enum ServiceMode {
    REPAIR = 'REPAIR',
    REPLACEMENT = 'REPLACEMENT',
    HOME_VISIT = 'HOME_VISIT',
    REMOTE_SUPPORT = 'REMOTE_SUPPORT',
}

export const CLAIM_STAGE_LABELS: Record<ClaimStage, string> = {
    [ClaimStage.CLAIM_SUBMITTED]: 'Claim submitted',
    [ClaimStage.VERIFIED_BY_MOGLIX]: 'Verified by Moglix',
    [ClaimStage.UNDER_BRAND_REVIEW]: 'Under brand review',
    [ClaimStage.CLAIM_APPROVED]: 'Claim approved',
    [ClaimStage.NOT_APPROVED]: 'Not approved',
    [ClaimStage.ACTION_NEEDED]: 'Action needed',
    [ClaimStage.DECLINED]: 'Declined',
    [ClaimStage.PICKUP_SCHEDULED]: 'Pickup scheduled',
    [ClaimStage.VISIT_SCHEDULED]: 'Visit scheduled',
    [ClaimStage.PICKED_UP_INSPECTION]: 'Picked up - inspection',
    [ClaimStage.ON_THE_WAY_SERVICE]: 'On the way - service',
    [ClaimStage.OUTCOME_DECLARED]: 'Outcome declared',
    [ClaimStage.CLOSED]: 'Closed',
    [ClaimStage.CLOSED_ALTERNATIVES_SHOWN]: 'Closed - alternatives shown',
    [ClaimStage.CANCELLED]: 'Cancelled',
};

export const CLAIM_STAGE_COLORS: Record<ClaimStage, string> = {
    [ClaimStage.CLAIM_SUBMITTED]: 'default',
    [ClaimStage.VERIFIED_BY_MOGLIX]: 'default',
    [ClaimStage.UNDER_BRAND_REVIEW]: 'blue',
    [ClaimStage.CLAIM_APPROVED]: 'green',
    [ClaimStage.NOT_APPROVED]: 'red',
    [ClaimStage.ACTION_NEEDED]: 'gold',
    [ClaimStage.DECLINED]: 'red',
    [ClaimStage.PICKUP_SCHEDULED]: 'cyan',
    [ClaimStage.VISIT_SCHEDULED]: 'cyan',
    [ClaimStage.PICKED_UP_INSPECTION]: 'cyan',
    [ClaimStage.ON_THE_WAY_SERVICE]: 'cyan',
    [ClaimStage.OUTCOME_DECLARED]: 'gold',
    [ClaimStage.CLOSED]: 'default',
    [ClaimStage.CLOSED_ALTERNATIVES_SHOWN]: 'default',
    [ClaimStage.CANCELLED]: 'default',
};

export const TERMINAL_CLAIM_STAGES = new Set<ClaimStage>([
    ClaimStage.CLOSED,
    ClaimStage.CLOSED_ALTERNATIVES_SHOWN,
    ClaimStage.CANCELLED,
]);

export interface ScheduledVisitInfo {
    mode?: ServiceMode;
    visitDate?: string;
    slot?: string;
    address?: string;
}

export interface TechnicianInfo {
    technicianId?: string;
    name?: string;
    phone?: string;
    rating?: number;
    hub?: string;
}

export interface ClaimTimelineEvent {
    stage: ClaimStage;
    note?: string;
    occurredAt: string;
}

export interface WarrantyClaimDTO {
    ticketId: string;
    requestId?: string;
    warrantyId: string;
    issueType: string;
    issueDescription?: string;
    mediaUrls?: string[];
    issueTimeframe?: string;
    stage: ClaimStage;
    expectedResolutionDate?: string;
    outcome?: ClaimOutcome;
    technicianInfo?: TechnicianInfo;
    scheduledVisit?: ScheduledVisitInfo;
    timeline: ClaimTimelineEvent[];
    createdBy?: string;
    updatedBy?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface ClaimStageTransitionRequest {
    toStage: ClaimStage;
    comment: string;
    updatedBy: string;
    expectedResolutionDate?: number;
    outcome?: ClaimOutcome;
    scheduledVisit?: ScheduledVisitInfo;
    technicianInfo?: TechnicianInfo;
}

export interface ServiceWarrantyFormValues {
    productId: string;
    serviceProductId: string;
    originalWarrantyMonths: number;
    price?: number;
    coveredParts?: string[];
    notCoveredParts?: string[];
    extensionOptions?: WarrantyExtensionOption[];
    claimReasons?: string[];
    claimRejectionReasons?: string[];
    resolutionModes?: string[];
}
